import { useState } from "react";
import { Reveal, SectionHeader } from "./Reveal";
import { Play } from "lucide-react";
import { PhotoMark } from "./PhotoMark";

const FEATURED_VIDEO = {
  song: "Reloj / La Barca",
  note: "Directo",
  videoId: "Vwfx1ijZP6w",
  poster: "https://i.ytimg.com/vi/Vwfx1ijZP6w/hqdefault.jpg",
};

const SIDE_VIDEOS = [
  {
    song: "Vacilón",
    note: "Directo",
    videoId: "OqZgnrlVZak",
    poster: "https://i.ytimg.com/vi/OqZgnrlVZak/hqdefault.jpg",
  },
  {
    song: "Rayito de Luna",
    note: "Bolero",
    videoId: "uDRk5JdEfig",
    poster: "https://i.ytimg.com/vi/uDRk5JdEfig/hqdefault.jpg",
  },
];

const MORE_VIDEOS = [
  { song: "Vagabundo", note: "Directo", watchUrl: "https://www.youtube.com/watch?v=KEgSRLpP0E0", poster: "https://i.ytimg.com/vi/KEgSRLpP0E0/hqdefault.jpg" },
  { song: "Como han pasado", note: "Directo", watchUrl: "https://www.youtube.com/watch?v=CJ2E0D0k7d0", poster: "https://i.ytimg.com/vi/CJ2E0D0k7d0/hqdefault.jpg" },
];

const yt = (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`;

const PlayButton = ({ size = "md" }) => (
  <span className={`relative inline-flex ${size === "lg" ? "h-20 w-20" : "h-14 w-14"} items-center justify-center`}>
    <span className="absolute inset-0 rounded-full bg-gold/60 animate-pulse-ring" aria-hidden="true" />
    <span className="relative inline-flex h-full w-full items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 group-hover:scale-110">
      <Play size={size === "lg" ? 26 : 18} className="translate-x-0.5" fill="currentColor" />
    </span>
  </span>
);

export const Live = () => {
  const [playing, setPlaying] = useState(false);
  const [playingId, setPlayingId] = useState(null);

  const openVideo = (v) => window.open(yt(v.q), "_blank", "noopener");

  return (
    <section id="directo" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <SectionHeader
          eyebrow="En Directo"
          title={
            <>
              Noche de bolero, <span className="italic text-gold">tal cual suena</span>.
            </>
          }
        />
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-sand leading-relaxed">
            Fragmentos del espectáculo «Con un solo corazón»: noventa minutos de bolero y son, sin artificios y con el público siempre dentro de la canción.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-8">
            <div data-testid="live-featured" className="group relative w-full aspect-video overflow-hidden bg-black">
              {playing ? (
                <iframe
                  data-testid="live-embed"
                  src={`https://www.youtube.com/embed/${FEATURED_VIDEO.videoId}?autoplay=1&rel=0`}
                  title={FEATURED_VIDEO.song}
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <button
                  data-testid="live-card-1"
                  onClick={() => setPlaying(true)}
                  className="group absolute inset-0 block w-full text-left"
                  aria-label={`Reproducir ${FEATURED_VIDEO.song}`}
                >
                  <img src={FEATURED_VIDEO.poster} alt={FEATURED_VIDEO.song} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  <PhotoMark className="top-5 right-5 h-14 w-auto opacity-90" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <PlayButton size="lg" />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{FEATURED_VIDEO.note}</p>
                      <h3 className="mt-2 font-serif text-3xl md:text-4xl text-cream">{FEATURED_VIDEO.song}</h3>
                    </div>
                    <p className="hidden sm:block text-[11px] uppercase tracking-[0.25em] text-cream/60">Reproducir vídeo</p>
                  </div>
                </button>
              )}
            </div>
          </Reveal>

          <div className="md:col-span-4 flex flex-col gap-6">
            {SIDE_VIDEOS.map((v, i) => (
              <Reveal key={v.song} delay={0.08 + i * 0.08} className="flex-1">
                <div data-testid={`live-card-${i + 2}`} className="group relative block w-full h-full min-h-[220px] overflow-hidden bg-black">
                  {v.videoId && playingId === v.videoId ? (
                    <iframe
                      data-testid={`live-embed-side-${i + 1}`}
                      src={`https://www.youtube.com/embed/${v.videoId}?autoplay=1&rel=0`}
                      title={v.song}
                      className="absolute inset-0 h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      onClick={() => (v.videoId ? setPlayingId(v.videoId) : openVideo(v))}
                      className="absolute inset-0 block w-full h-full text-left"
                      aria-label={`Reproducir ${v.song}`}
                    >
                      <img src={v.poster} alt={v.song} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      <div className="absolute inset-0 bg-ink/55 transition-colors duration-500 group-hover:bg-ink/40" />
                      <PhotoMark className="top-4 right-4 h-10 w-auto opacity-90" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <PlayButton />
                      </div>
                      <div className="absolute bottom-0 inset-x-0 p-5">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{v.note}</p>
                        <h3 className="mt-1.5 font-serif text-2xl text-cream">{v.song}</h3>
                      </div>
                    </button>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 md:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {MORE_VIDEOS.map((v, i) => (
              <Reveal key={v.videoId} delay={0.1 + i * 0.06}>
                <div data-testid={`live-card-more-${i + 1}`} className="group relative w-full aspect-video overflow-hidden bg-black">
                  {playingId === v.videoId ? (
                    <iframe
                      data-testid={`live-embed-more-${i + 1}`}
                      src={`https://www.youtube.com/embed/${v.videoId}?autoplay=1&rel=0`}
                      title={v.song}
                      className="absolute inset-0 h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      onClick={() => (v.videoId ? setPlayingId(v.videoId) : window.open(v.watchUrl, "_blank", "noopener"))}
                      aria-label={`Reproducir ${v.song}`}
                      className="absolute inset-0 block w-full h-full text-left"
                    >
                      <img src={v.poster} alt={v.song} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      <div className="absolute inset-0 bg-ink/55 transition-colors duration-500 group-hover:bg-ink/40" />
                      <PhotoMark className="top-4 right-4 h-10 w-auto opacity-90" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <PlayButton />
                      </div>
                      <div className="absolute bottom-0 inset-x-0 p-5">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{v.note}</p>
                        <h3 className="mt-1.5 font-serif text-2xl text-cream">{v.song}</h3>
                      </div>
                    </button>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
